import {
  Component,
  computed,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { legalRelationship } from 'src/app/components/dashboard/enums/enum-descriptors';
import {
  SaveOrResendProFormaDialogComponent,
  SaveOrResendProFormaDialogResult,
  getFeeChangeFollowUpKind,
  isPrincipalClaimAmountDecrease,
} from 'src/app/helper/save-or-resend-pro-forma-dialog/save-or-resend-pro-forma-dialog.component';
import { EnumsService } from 'src/app/services/enums/enums.service';
import { HelpersService } from 'src/app/services/helpers/helpers.service';
import { PayeeCasesService } from 'src/app/shared/payee-cases/payee-cases.service';
import { CaseTableItem } from 'src/app/shared/payee-cases/types';
import { LegalRelationship } from 'src/types';
import { EditCaseDialogService } from './edit-case-dialog.service';
import { PayeePopupComponent } from '../../shared/payee-popup/payee-popup.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
  MatExpansionPanelDescription,
} from '@angular/material/expansion';
import { CaseFormComponent } from './case-form/case-form.component';
import { AttorneyFormComponent } from './attorney-form/attorney-form.component';
import { ExternalIdsFormComponent } from './external-ids-form/external-ids-form.component';
import { ClaimsFormComponent } from './claims-form/claims-form.component';
import { ClientFormComponent } from './client-form/client-form.component';
import { DebtorsFormComponent } from './debtors-form/debtors-form.component';
import { PayeeButtonComponent } from '../../shared/payee-buttons/payee-button/payee-button.component';
import { PayeeIconComponent } from '../../shared/payee-icon/payee-icon.component';
import {
  createClaimsGenerateForm,
  createClientGenerateForm,
  createDebtorsGenerateForm,
  createLegalRelationshipGenerateForm,
  generateMissingLabels,
} from './generate-completeness';

@Component({
  selector: 'app-edit-case-dialog',
  templateUrl: './edit-case-dialog.component.html',
  styleUrls: ['./edit-case-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    MatProgressSpinner,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatExpansionPanelDescription,
    CaseFormComponent,
    AttorneyFormComponent,
    ExternalIdsFormComponent,
    ClaimsFormComponent,
    ClientFormComponent,
    DebtorsFormComponent,
    PayeeButtonComponent,
    PayeeIconComponent,
  ],
})
export class EditCaseDialogComponent implements OnInit, OnDestroy {
  private snackbar = inject(MatSnackBar);
  private editCaseDialogService = inject(EditCaseDialogService);
  private enumsService = inject(EnumsService);
  private helpersService = inject(HelpersService);
  private matDialog = inject(MatDialog);
  private translate = inject(TranslateService);
  private payeeCasesService = inject(PayeeCasesService);
  private readonly destroy = new Subject<void>();

  dialogRef = inject<MatDialogRef<EditCaseDialogComponent>>(MatDialogRef);
  data = inject<CaseTableItem>(MAT_DIALOG_DATA);

  get form() {
    return this.editCaseDialogService.form;
  }

  readonly initialized = signal(false);
  readonly submitting = signal(false);
  readonly legalRelationship = signal<string | undefined>(undefined);
  readonly legalRelationshipGenerateForm = signal<FormGroup | null>(null);
  readonly claimsGenerateForm = signal<FormArray | null>(null);
  readonly clientGenerateForm = signal<FormGroup | null>(null);
  readonly debtorsGenerateForm = signal<FormArray | null>(null);
  readonly legalRelationshipMissing = computed(() => {
    const form = this.legalRelationshipGenerateForm();
    return form ? generateMissingLabels(form)[0] : undefined;
  });
  readonly claimsMissing = computed(() => {
    const form = this.claimsGenerateForm();
    return form ? generateMissingLabels(form)[0] : undefined;
  });
  readonly clientMissing = computed(() => {
    const form = this.clientGenerateForm();
    return form ? generateMissingLabels(form)[0] : undefined;
  });
  readonly debtorsMissing = computed(() => {
    const form = this.debtorsGenerateForm();
    return form ? generateMissingLabels(form)[0] : undefined;
  });

  get legalRelationshipSpecification() {
    return this.form.value.legal_relationship?.specification;
  }
  legalRelationships: LegalRelationship[] = [];

  get legal_relationship() {
    return this.form.controls.legal_relationship;
  }

  hasAiFilledIn(root: AbstractControl): boolean {
    return this.editCaseDialogService.hasAiFilledIn(root);
  }

  get client() {
    return this.form.controls.client;
  }
  get claims() {
    return this.form.controls.claims;
  }
  get debtors() {
    return this.form.controls.debtors;
  }
  get clientName() {
    return this.form.value.client?.name;
  }
  get isClientDuplicated(): boolean {
    return !!this.editCaseDialogService.data?.client.is_duplicated;
  }
  get debtorName() {
    const debtors = this.form.value.debtors;
    return debtors?.[0]?.name;
  }

  get isClientGenerateComplete(): boolean {
    return this.clientGenerateForm()?.valid ?? false;
  }

  get isLegalRelationshipGenerateComplete(): boolean {
    return this.legalRelationshipGenerateForm()?.valid ?? false;
  }

  get isClaimsGenerateComplete(): boolean {
    return this.claimsGenerateForm()?.valid ?? false;
  }

  get isDebtorsGenerateComplete(): boolean {
    return this.debtorsGenerateForm()?.valid ?? false;
  }

  async ngOnInit(): Promise<void> {
    this.form.controls.legal_relationship.controls.type_id.valueChanges
      .pipe(takeUntil(this.destroy))
      .subscribe((type_id: number | null) => {
        this.legalRelationship.set(
          this.legalRelationships.find((lr) => lr.id === type_id)?.label,
        );
      });

    try {
      await Promise.allSettled([
        this.enumsService
          .getEnum<LegalRelationship>(legalRelationship.model)
          .then((v) => (this.legalRelationships = v)),
        this.editCaseDialogService.setForm(this.data.id),
        this.editCaseDialogService.loadReferenceData(),
      ]);
      const typeId =
        this.form.controls.legal_relationship.controls.type_id.value;
      this.legalRelationship.set(
        this.legalRelationships.find((lr) => lr.id === typeId)?.label,
      );
      this.refreshGenerateCompleteness();
      this.form.valueChanges
        .pipe(takeUntil(this.destroy))
        .subscribe(() => this.refreshGenerateCompleteness());
      this.initialized.set(true);
    } catch (error) {
      console.error('Error while setting form', error);
      this.snackbar.open('Valami hiba történt az ügy betöltésekor!', 'OK', {
        duration: 5000,
      });
      this.cancel();
    }
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  private refreshGenerateCompleteness(): void {
    const value = this.form.getRawValue();

    this.legalRelationshipGenerateForm.set(
      createLegalRelationshipGenerateForm(value.legal_relationship),
    );
    this.claimsGenerateForm.set(createClaimsGenerateForm(value.claims));
    this.clientGenerateForm.set(
      createClientGenerateForm(
        value.client,
        this.editCaseDialogService.partyTypes,
        this.editCaseDialogService.representativeTypes,
        this.editCaseDialogService.countries,
      ),
    );

    this.debtorsGenerateForm.set(
      createDebtorsGenerateForm(
        value.debtors,
        this.editCaseDialogService.partyTypes,
        this.editCaseDialogService.representativeTypes,
        this.editCaseDialogService.countries,
      ),
    );
  }

  cancel(): void {
    if (this.submitting()) {
      return;
    }

    this.dialogRef.close();
  }

  async submit(): Promise<void> {
    if (this.submitting()) {
      return;
    }

    if (this.form.invalid || !this.data) {
      this.helpersService.markAllChildrenAsDirty(this.form);
      return;
    }

    const kind = this.willFeesChange()
      ? getFeeChangeFollowUpKind(this.data, {
          feeDecrease: this.isFeeDecrease(),
          feeIncrease: !this.isFeeDecrease(),
        })
      : null;

    try {
      switch (kind) {
        case 'proforma':
          if (!(await this.handleProFormaFollowUp())) {
            return;
          }
          break;
        case 'invoice_asana':
          if (!(await this.handleAsanaFollowUp())) {
            return;
          }
          break;
        default:
          await this.performSubmit();
      }

      this.dialogRef.close(true);
    } catch (error) {
      console.error('Error while submitting form', error);
    }
  }

  private async performSubmit(): Promise<void> {
    this.submitting.set(true);

    try {
      await this.editCaseDialogService.submit();
    } finally {
      this.submitting.set(false);
    }
  }

  private async handleProFormaFollowUp(): Promise<boolean> {
    const descriptionKey = this.isFeeDecrease()
      ? 'CASES_TABLE.CASE_ACTIONS.RESEND_PROFORMA_CONFIRM'
      : 'CASES_TABLE.CASE_ACTIONS.CREATE_FEE_CHANGE_INVOICE_ASANA_TASK_INCREASE_CONFIRM';
    const choice = await this.askSaveOrSend(
      'CASES_TABLE.CASE_ACTIONS.RESEND_PROFORMA',
      descriptionKey,
      true,
    );
    if (choice === undefined) {
      return false;
    }

    try {
      await this.performSubmit();
      this.snackbar.open(
        this.translate.instant(
          'CASES_TABLE.CASE_ACTIONS.RESEND_PROFORMA_SUCCESS',
        ),
        'OK',
        { duration: 2000 },
      );
      this.payeeCasesService.setCasesSubject.next();
    } catch {
      this.snackbar.open(
        this.translate.instant(
          'CASES_TABLE.CASE_ACTIONS.RESEND_PROFORMA_ERROR',
        ),
        'OK',
        { duration: 10_000 },
      );
      return false;
    }

    return true;
  }

  private async handleAsanaFollowUp(): Promise<boolean> {
    const choice = await this.askSaveOrSend(
      'CASES_TABLE.CASE_ACTIONS.CREATE_FEE_CHANGE_INVOICE_ASANA_TASK',
      'CASES_TABLE.CASE_ACTIONS.CREATE_FEE_CHANGE_INVOICE_ASANA_TASK_CONFIRM',
      true,
    );
    if (choice === undefined) {
      return false;
    }

    try {
      await this.performSubmit();
      this.snackbar.open(
        this.translate.instant(
          'CASES_TABLE.CASE_ACTIONS.CREATE_FEE_CHANGE_INVOICE_ASANA_TASK_SUCCESS',
        ),
        'OK',
        { duration: 2000 },
      );
      this.payeeCasesService.setCasesSubject.next();
    } catch {
      this.snackbar.open(
        this.translate.instant(
          'CASES_TABLE.CASE_ACTIONS.CREATE_FEE_CHANGE_INVOICE_ASANA_TASK_ERROR',
        ),
        'OK',
        { duration: 10_000 },
      );
      return false;
    }

    return true;
  }

  private async askSaveOrSend(
    titleKey: string,
    descriptionKey: string,
    autoSendOnSave = false,
  ): Promise<SaveOrResendProFormaDialogResult | undefined> {
    return firstValueFrom(
      this.matDialog
        .open(SaveOrResendProFormaDialogComponent, {
          width: '520px',
          data: {
            title: this.translate.instant(titleKey),
            description: this.translate.instant(descriptionKey),
            autoSendOnSave,
          },
        })
        .afterClosed(),
    );
  }

  private willFeesChange(): boolean {
    return this.claims.controls.some(
      (c) =>
        c.controls.remaining_amount.dirty || c.controls.original_amount.dirty,
    );
  }

  private isFeeDecrease(): boolean {
    const previousClaims = this.editCaseDialogService.data?.claims ?? [];
    const currentClaims = this.claims.controls.map((control) => ({
      id: control.controls.id.value,
      remaining_amount: control.controls.remaining_amount.value,
    }));

    return isPrincipalClaimAmountDecrease(previousClaims, currentClaims);
  }
}
